import { dev } from '$app/environment';
import { apiStore, resetApiStore } from '$store/api';
import { Capacitor } from '@capacitor/core';
import type { GetCurrentSessionResponse } from '@cc/classmate-sdk';
import { setUser as sentrySetUser } from "@sentry/sveltekit";
import { V2 } from 'src/services/sdk-client';
import { persisted } from 'svelte-local-storage-store';
import { derived, get } from 'svelte/store';
import { mobileStore } from '../kernel/stores/mobile.store';

/**
 * Type representing the session store data.
 * It can either be null or contain session data along with optional unread messages and notification count.
 */
export type SessionStoreType = null | GetCurrentSessionResponse & {
	unreadMessages?: number;
	notificationCount?: number;
	hasSetupProfile?: boolean;
};

/**
 * Persistent store for session data, stored in local storage.
 */
export const sessionStore = persisted<Partial<SessionStoreType>>(`CC_V3_SESSION`, null);

/**
 * Derived store that indicates whether the user is logged in based on the session store.
 */
export const isLoggedIn = derived(sessionStore, ($session) => $session !== null && $session.id !== undefined);	

/**
 * Logs out the user by calling the Logout function, clearing cookies and local storage,
 * and redirecting to the appropriate path based on the environment.
 */
export async function logout() {
	try {
		await V2.getV2AuthSessionLogout();

		
		if(Capacitor.isNativePlatform()) {
			resetApiStore();
			setSession(null);

			window.location.href = '/';
		}else{
			setSession(null);
			window.location.href = '/public/login';
		}
	} catch (error) {
		console.error('Logout error:', error);
	} finally {
		setSession(null);
	}
}

/**
 * Sets the session data in the session store and updates Sentry user context if not in development mode.
 * 
 * @param sessionData - The session data to set in the store.
 */
export function setSession(sessionData: SessionStoreType) {
	if (sessionData) {
		sessionStore.set({...sessionData, unreadMessages: 0, notificationCount: 0, hasSetupProfile: true});

		const tenantApiUrl = get(apiStore)?.apiUrl;
		const mobileStoreValue = get(mobileStore);

		if(!dev){
			sentrySetUser({ email: sessionData.email, id: sessionData.id, deployment: {tenantApiUrl, ...mobileStoreValue} });
		}

		return;
	}

	sessionStore.set(null);
	sentrySetUser(null);
}

/**
 * Updates the notification count and unread messages in the session store.
 * 
 * @param notificationCount - The new notification count.
 * @param unreadMessages - The new unread messages count (optional).
 */
export function sessionSetNotificationCount(notificationCount: number, unreadMessages?: number) {
	sessionStore.update((old) => ({ ...old, notificationCount, unreadMessages: unreadMessages ?? 0 }));
}